/* ExpertsHub — your team of AI experts (Stella, June, Lou, Dr. Elin).
 * Lives under Drömmar -> Experter tab. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send, Loader2, Sparkles, Trash2, ArrowLeft, Settings as SettingsIcon,
  Palette, BookOpen, Brush, Brain, X, Bone, AlertTriangle, Upload, Camera, ShoppingBag, Crown, Scissors, Home, Salad, Ruler, ChefHat, Heart, Bookmark,
} from 'lucide-react';
import PortraitCropModal from './PortraitCropModal';
import LouHairstyles from './LouHairstyles';
import LouCapsuleWardrobe from './LouCapsuleWardrobe';
import LouVisualBoards from './LouVisualBoards';
import LouMakeupAnalysis from './LouMakeupAnalysis';
import { notifyLlmFailure } from './LlmBudgetBanner';

const looksLikeLlmError = (msg) => {
  const s = String(msg || '').toLowerCase();
  return /batteri|saldo|budget|quota|402|authentic|nyckel/i.test(s);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== EXPERT CHAT VIEW ==============
function ExpertChat({ expert, onBack, onOpenSpecial }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [portraitBusy, setPortraitBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [hairOpen, setHairOpen] = useState(false);
  const [capsuleOpen, setCapsuleOpen] = useState(false);
  const [visualBoardsOpen, setVisualBoardsOpen] = useState(false);
  const [makeupOpen, setMakeupOpen] = useState(false);
  const [farmorLibOpen, setFarmorLibOpen] = useState(false);
  const [farmorLib, setFarmorLib] = useState([]);
  const [postureOpen, setPostureOpen] = useState(false);
  const [postureData, setPostureData] = useState(null);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/experts/${expert.id}/messages`, { withCredentials: true });
      setMessages(r.data?.items || []);
    } catch {/**/}
  }, [expert.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(''); setBusy(true);
    setMessages((m) => [...m, { role: 'user', text }]);
    try {
      const r = await axios.post(`${API}/experts/${expert.id}/chat`, { text }, { withCredentials: true });
      setMessages((m) => [...m, { role: 'expert', text: r.data?.reply || '' }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Något gick fel';
      toast.error(detail);
      if (looksLikeLlmError(detail) || e?.response?.status === 402 || e?.response?.status === 502) {
        notifyLlmFailure(detail);
      }
      setMessages((m) => [...m, { role: 'expert', text: '…verkar som mitt batteri sinade just nu 💔 (LLM-saldot)' }]);
    } finally { setBusy(false); }
  };

  const sendImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || busy) return;
    setBusy(true);
    const caption = input.trim();
    setInput('');
    const previewUrl = URL.createObjectURL(f);
    setMessages((m) => [...m, { role: 'user', text: caption || '[skickade en bild 📸]', image_preview: previewUrl }]);
    try {
      const fd = new FormData();
      fd.append('file', f);
      if (caption) fd.append('caption', caption);
      const r = await axios.post(`${API}/experts/${expert.id}/chat/image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      setMessages((m) => [...m, { role: 'expert', text: r.data?.reply || '' }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte tolka bilden');
      setMessages((m) => [...m, { role: 'expert', text: '…jag försökte men kunde inte se bilden riktigt 💔' }]);
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm('Sudda hela konversationen?')) return;
    try {
      await axios.post(`${API}/experts/${expert.id}/reset`, {}, { withCredentials: true });
      setMessages([]);
    } catch {/**/}
  };

  const genPortrait = async () => {
    setPortraitBusy(true);
    try {
      const r = await axios.post(`${API}/experts/${expert.id}/portrait`, {}, { withCredentials: true });
      expert.portrait_b64 = r.data?.portrait_b64;
      expert.portrait_mime = r.data?.portrait_mime;
      toast.success('Porträtt klart ✨');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte');
    } finally { setPortraitBusy(false); }
  };

  const uploadPortrait = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setCropFile(f);
  };

  const commitPortrait = async (blob) => {
    setPortraitBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', new File([blob], 'portrait.jpg', { type: 'image/jpeg' }));
      const r = await axios.post(`${API}/experts/${expert.id}/portrait/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      expert.portrait_b64 = r.data?.portrait_b64;
      expert.portrait_mime = r.data?.portrait_mime;
      toast.success('Bilden uppladdad ✨');
      setCropFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte ladda upp');
    } finally { setPortraitBusy(false); }
  };

  const specialBtn = {
    lou: { icon: Palette, label: 'Färganalys', key: 'lou' },
    june: { icon: BookOpen, label: 'Bujo-mall', key: 'june' },
    stella: { icon: Brush, label: 'Städ-plan', key: 'stella' },
    elin: { icon: Brain, label: 'KBT-strategi', key: 'elin' },
    fia: { icon: Bone, label: 'Lös låsning', key: 'fia' },
    farmor: { icon: Home, label: 'Husmorstips', key: 'farmor' },
    hedda: { icon: Salad, label: 'Måltidsförslag', key: 'hedda' },
    astrid: { icon: ChefHat, label: 'Recept', key: 'astrid' },
  }[expert.id];

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden" data-testid={`expert-chat-${expert.id}`}>
      <div className={`bg-gradient-to-r ${expert.color} px-4 py-3 text-white flex items-center justify-between`}>
        <div className="flex items-center gap-2.5">
          <button onClick={onBack} data-testid="expert-back" className="p-1.5 rounded-full bg-white/15 hover:bg-white/25"><ArrowLeft className="w-4 h-4" /></button>
          <div className="w-10 h-10 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-base overflow-hidden border-2 border-white/40">
            {expert.portrait_b64 ? (
              <img src={`data:${expert.portrait_mime || 'image/png'};base64,${expert.portrait_b64}`} alt={expert.name} className="w-full h-full object-cover" />
            ) : (
              <span>{expert.emoji}</span>
            )}
          </div>
          <div>
            <div className="font-extrabold text-base leading-tight">{expert.name}</div>
            <div className="text-[10px] opacity-90">{expert.title}</div>
          </div>
        </div>
        <div className="flex gap-1">
          {specialBtn && (
            <button onClick={() => onOpenSpecial(specialBtn.key)} data-testid={`expert-special-${expert.id}`} className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" title={specialBtn.label}>
              <specialBtn.icon className="w-3 h-3" /> <span className="hidden sm:inline">{specialBtn.label}</span>
            </button>
          )}
          {expert.id === 'farmor' && (
            <button onClick={() => setFarmorLibOpen(true)} data-testid="expert-farmor-library" className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" title="Farmors bibliotek">
              📚 <span className="hidden sm:inline">Bibliotek</span>
            </button>
          )}
          {expert.id === 'lou' && (
            <button onClick={() => setHairOpen(true)} data-testid="expert-lou-hair" className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" title="Frisyrer">
              <Scissors className="w-3 h-3" /> <span className="hidden sm:inline">Frisyrer</span>
            </button>
          )}
          {expert.id === 'lou' && (
            <button onClick={() => setCapsuleOpen(true)} data-testid="expert-lou-capsule" className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" title="Personlig kapselgarderob">
              🧺 <span className="hidden sm:inline">Kapselgarderob</span>
            </button>
          )}
          {expert.id === 'lou' && (
            <button onClick={() => setVisualBoardsOpen(true)} data-testid="expert-lou-boards" className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" title="Visual boards: outfits, mood, vision, matcha">
              🎨 <span className="hidden sm:inline">Boards</span>
            </button>
          )}
          {expert.id === 'lou' && (
            <button onClick={() => setMakeupOpen(true)} data-testid="expert-lou-makeup" className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" title="Smink-analys baserad på dina drag">
              💄 <span className="hidden sm:inline">Smink</span>
            </button>
          )}
          {expert.id === 'fia' && (
            <button
              onClick={async () => {
                if (!postureData) {
                  try {
                    const r = await axios.post(`${API}/experts/fia/posture`, {}, { withCredentials: true });
                    setPostureData(r.data);
                  } catch (e) {
                    toast.error('Kunde inte ladda hållnings-rutinen');
                    return;
                  }
                }
                setPostureOpen(true);
              }}
              data-testid="expert-fia-posture"
              className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1"
              title="Höj-dig — 5-min hållnings-rutin"
            >
              <Ruler className="w-3 h-3" /> <span className="hidden sm:inline">Höj-dig</span>
            </button>
          )}
          {expert.id === 'astrid' && (
            <button
              onClick={() => setFavoritesOpen(true)}
              data-testid="expert-astrid-favorites"
              className="px-2 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1"
              title="Mina sparade recept"
            >
              <Bookmark className="w-3 h-3" /> <span className="hidden sm:inline">Favoriter</span>
            </button>
          )}
          <button onClick={genPortrait} disabled={portraitBusy} data-testid="expert-portrait" className="p-1.5 rounded-full bg-white/15 hover:bg-white/25" title="Skapa porträtt med AI">
            {portraitBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </button>
          <label data-testid={`expert-portrait-upload-label-${expert.id}`} className={`p-1.5 rounded-full bg-white/15 hover:bg-white/25 cursor-pointer ${portraitBusy ? 'opacity-50 pointer-events-none' : ''}`} title="Ladda upp egen bild">
            <Upload className="w-4 h-4" />
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPortrait} disabled={portraitBusy} data-testid={`expert-portrait-upload-${expert.id}`} className="hidden" />
          </label>
          <button onClick={reset} data-testid="expert-reset" className="p-1.5 rounded-full bg-white/15 hover:bg-white/25" title="Rensa"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div ref={scrollRef} className="px-4 py-3 h-[420px] overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 px-4">
            <div className="text-4xl mb-2">{expert.emoji}</div>
            <p className="text-sm font-bold text-gray-600">Säg hej till {expert.name}</p>
            <p className="text-xs italic mt-1">Hen är expert på {expert.title.toLowerCase()}</p>
            {specialBtn && (
              <button onClick={() => onOpenSpecial(specialBtn.key)} className="mt-3 px-3 py-1.5 rounded-full bg-gradient-to-r from-violet-400 to-rose-400 text-white text-xs font-bold">
                Eller börja med {specialBtn.label.toLowerCase()} →
              </button>
            )}
          </div>
        )}
        {messages.map((m, i) => {
          const imgSrc = m.image_preview || (m.image_b64 ? `data:${m.image_mime || 'image/jpeg'};base64,${m.image_b64}` : null);
          return (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`expert-msg-${m.role}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-snug ${m.role === 'user' ? 'bg-violet-500 text-white rounded-br-md' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'}`}>
              {m.role === 'expert' && <div className="text-[10px] uppercase font-bold opacity-70 mb-0.5">{expert.name}</div>}
              {imgSrc && (
                <img src={imgSrc} alt="bifogad" className="rounded-xl max-h-48 max-w-full object-cover mb-1.5 border border-white/40" />
              )}
              {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
            </div>
          </div>
          );
        })}
        {busy && (
          <div className="flex justify-start mb-2">
            <div className="bg-white text-gray-400 px-3 py-2 rounded-2xl rounded-bl-md text-sm shadow-sm border border-gray-200">
              <span className="inline-flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="px-3 py-3 bg-white border-t border-gray-200 flex gap-2 items-end">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder={`Fråga ${expert.name}…`}
          data-testid="expert-input"
          className="flex-1 px-3 py-2 rounded-2xl border-2 border-gray-200 focus:border-violet-500 outline-none text-sm resize-none max-h-32"
        />
        <label
          data-testid={`expert-image-attach-label-${expert.id}`}
          title="Skicka en bild"
          className={`px-3 py-2.5 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-400 hover:from-violet-500 hover:to-fuchsia-500 text-white shadow cursor-pointer flex items-center ${busy ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <span className="text-base leading-none">📸</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={sendImage} disabled={busy} data-testid={`expert-image-attach-${expert.id}`} className="hidden" />
        </label>
        <button onClick={send} disabled={!input.trim() || busy} data-testid="expert-send" className="px-3 py-2.5 rounded-2xl bg-violet-500 hover:bg-violet-600 text-white disabled:opacity-50 shadow">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {cropFile && (
        <PortraitCropModal
          file={cropFile}
          title={`Beskär ${expert.name}-bild`}
          accent="rose"
          onCancel={() => setCropFile(null)}
          onConfirm={commitPortrait}
        />
      )}
      {hairOpen && <LouHairstyles onClose={() => setHairOpen(false)} />}
      {capsuleOpen && <LouCapsuleWardrobe onClose={() => setCapsuleOpen(false)} />}
      {visualBoardsOpen && <LouVisualBoards onClose={() => setVisualBoardsOpen(false)} />}
      {makeupOpen && <LouMakeupAnalysis onClose={() => setMakeupOpen(false)} />}
      {farmorLibOpen && (
        <FarmorLibraryModal
          onClose={() => setFarmorLibOpen(false)}
          items={farmorLib}
          setItems={setFarmorLib}
        />
      )}
      {postureOpen && postureData && (
        <PostureBoostModal
          data={postureData}
          onClose={() => setPostureOpen(false)}
        />
      )}
      {favoritesOpen && (
        <AstridFavoritesModal onClose={() => setFavoritesOpen(false)} />
      )}
    </div>
  );
}

// ============== POSTURE / HÖJ-DIG MODAL ==============
function PostureBoostModal({ data, onClose }) {
  const [step, setStep] = React.useState(-1); // -1 = intro, 0..N = exercise, N = done
  const [secondsLeft, setSecondsLeft] = React.useState(0);
  const [running, setRunning] = React.useState(false);

  const exercises = data.exercises || [];
  const total = exercises.length;
  const current = step >= 0 && step < total ? exercises[step] : null;

  React.useEffect(() => {
    if (!running || !current) return undefined;
    if (secondsLeft <= 0) {
      if (step < total - 1) {
        setStep(step + 1);
        setSecondsLeft(exercises[step + 1].duration_sec || 60);
      } else {
        setRunning(false);
        setStep(total); // done
      }
      return undefined;
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [running, secondsLeft, step, current, exercises, total]);

  const start = () => {
    setStep(0);
    setSecondsLeft(exercises[0].duration_sec || 60);
    setRunning(true);
  };
  const skipToNext = () => {
    if (step < total - 1) {
      setStep(step + 1);
      setSecondsLeft(exercises[step + 1].duration_sec || 60);
    } else {
      setStep(total);
      setRunning(false);
    }
  };
  const pause = () => setRunning((r) => !r);

  const done = step === total;

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="posture-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-sky-200" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-sky-400 to-cyan-500 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Dr. Fia</div>
            <h2 className="text-xl font-bold font-['Fredoka']">{data.title}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-full bg-white/20 hover:bg-white/30" data-testid="posture-close" aria-label="Stäng">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === -1 && (
            <div className="space-y-3" data-testid="posture-intro">
              <p className="text-sm text-sky-900 leading-relaxed">{data.intro}</p>
              <div className="bg-sky-50 border-2 border-sky-100 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase text-sky-700 mb-1">Så här fungerar det</div>
                <p className="text-xs text-sky-900 leading-relaxed">{data.why_it_works}</p>
              </div>
              <div className="text-[11px] italic text-sky-800 px-1">💡 När: {data.when_to_use}</div>
              <button
                onClick={start}
                data-testid="posture-start"
                className="w-full py-3 rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold flex items-center justify-center gap-2 shadow-md"
              >
                <Sparkles className="w-4 h-4" /> Starta {data.duration_min}-min rutinen
              </button>
              <div className="border-t border-sky-100 pt-2">
                <div className="text-[10px] font-bold uppercase text-sky-700 mb-1">Alla 4 övningar</div>
                <ul className="text-xs text-sky-900 space-y-1">
                  {exercises.map((ex, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="opacity-60">{i + 1}.</span>
                      <span>{ex.name}</span>
                      <span className="text-[10px] opacity-60 ml-auto">{ex.duration_sec}s{ex.reps ? ` × ${ex.reps.split('×')[0].trim()}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {current && (
            <div className="space-y-3" data-testid="posture-exercise">
              <div className="flex items-center justify-between text-[10px] text-sky-600 font-bold uppercase">
                <span>Övning {step + 1} / {total}</span>
                <span>{current.target}</span>
              </div>
              <h3 className="text-2xl font-bold font-['Fredoka'] text-sky-900">{current.name}</h3>

              <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border-2 border-sky-200 rounded-2xl p-4 text-center" data-testid="posture-timer">
                <div className="text-6xl font-bold tabular-nums text-sky-700">{secondsLeft}s</div>
                {current.reps && <div className="text-[11px] text-sky-600 mt-1">{current.reps}</div>}
              </div>

              <div className="bg-white border border-sky-100 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase text-sky-700 mb-1">Så gör du</div>
                <p className="text-sm text-gray-800 leading-relaxed">{current.how_to}</p>
              </div>

              <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-2.5 text-xs text-cyan-900">
                🌬️ <b>Andning:</b> {current.cue}
              </div>

              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-2.5 text-xs text-emerald-900">
                💡 <b>Varför:</b> {current.why}
              </div>

              {current.safety && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-900 flex gap-2 items-start">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{current.safety}</span>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button onClick={pause} data-testid="posture-pause" className="flex-1 py-2 rounded-full bg-sky-100 text-sky-900 font-bold text-xs hover:bg-sky-200">
                  {running ? '⏸ Pausa' : '▶ Fortsätt'}
                </button>
                <button onClick={skipToNext} data-testid="posture-skip" className="flex-1 py-2 rounded-full bg-white border border-sky-200 text-sky-700 font-bold text-xs hover:bg-sky-50">
                  {step < total - 1 ? 'Nästa →' : 'Klart ✨'}
                </button>
              </div>
            </div>
          )}

          {done && (
            <div className="text-center space-y-3" data-testid="posture-done">
              <div className="text-5xl">🌱📏✨</div>
              <h3 className="text-xl font-bold font-['Fredoka'] text-sky-900">Klart! Du är {data.duration_min} min högre nu.</h3>
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-3 text-left">
                <div className="text-[10px] font-bold uppercase text-purple-700 mb-1">🌙 Aura-bonus</div>
                <p className="text-xs text-purple-900 leading-relaxed">{data.aura_bonus}</p>
              </div>
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 text-left">
                <div className="text-[10px] font-bold uppercase text-amber-700 mb-1">💪 7-dagars utmaning</div>
                <p className="text-xs text-amber-900 leading-relaxed">{data.daily_challenge}</p>
              </div>
              <button onClick={onClose} data-testid="posture-finish" className="w-full py-3 rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold">
                Bär det med mig 💜
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============== SPECIAL MODALS ==============
function LouStyleAnalysis({ existing, onClose: _onClose }) {
  // Photo-based deep analysis: archetype + Kibbe + palette + shopping
  const [facePhoto, setFacePhoto] = useState(null);     // {file, preview}
  const [bodyPhotos, setBodyPhotos] = useState([]);     // array of {file, preview}
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(existing || null);
  const [checked, setChecked] = useState(() => {
    const raw = existing?.checklist_checked || {};
    // unescape keys from mongo dot-safe form
    const out = {};
    Object.entries(raw).forEach(([k, v]) => {
      out[k.replace(/_DOT_/g, '.').replace(/_USD_/g, '$')] = v;
    });
    return out;
  });

  const toggleCheck = async (cat, item) => {
    const key = `${cat}::${item}`;
    const next = !checked[key];
    setChecked((c) => ({ ...c, [key]: next }));
    try {
      await axios.post(`${API}/experts/lou/style-checklist/toggle`, { key, checked: next }, { withCredentials: true });
    } catch {
      // rollback on failure
      setChecked((c) => ({ ...c, [key]: !next }));
      toast.error('Kunde inte spara');
    }
  };

  useEffect(() => {
    if (!result?.checklist_checked) return;
    const out = {};
    Object.entries(result.checklist_checked).forEach(([k, v]) => {
      out[k.replace(/_DOT_/g, '.').replace(/_USD_/g, '$')] = v;
    });
    setChecked(out);
  }, [result]);

  const pickFace = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFacePhoto({ file: f, preview: URL.createObjectURL(f) });
  };

  const pickBody = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    setBodyPhotos(files.map((f) => ({ file: f, preview: URL.createObjectURL(f) })));
  };

  const run = async () => {
    if (!facePhoto && bodyPhotos.length === 0) {
      toast.error('Ladda upp minst en bild 💄');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (facePhoto) fd.append('face_photo', facePhoto.file);
      bodyPhotos.forEach((b) => fd.append('body_photos', b.file));
      if (notes) fd.append('notes', notes);
      const r = await axios.post(`${API}/experts/lou/style-analysis`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(r.data);
      toast.success('Stilanalys klar 💋✨');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte analysera bilderna');
    } finally { setBusy(false); }
  };

  if (busy) {
    return (
      <div className="p-6 text-center space-y-3" data-testid="lou-style-busy">
        <Loader2 className="w-10 h-10 animate-spin text-rose-400 mx-auto" />
        <p className="text-sm text-rose-700 font-bold">Lou tittar på bilderna…</p>
        <p className="text-[11px] text-gray-500 italic">Det här tar 20-60 sekunder ✨</p>
      </div>
    );
  }

  if (result) {
    const fa = result.feminine_archetype || {};
    const kb = result.kibbe_type || {};
    const cp = result.color_palette || {};
    return (
      <div className="space-y-3" data-testid="lou-style-result">
        {result.summary && (
          <p className="text-sm bg-rose-50 border border-rose-200 rounded-2xl px-3 py-2 italic text-rose-800">💖 {result.summary}</p>
        )}
        {fa.primary && (
          <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl border-2 border-violet-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-violet-500 font-extrabold flex items-center gap-1"><Crown className="w-3 h-3" /> Feminin arketyp</div>
            <h3 className="text-xl font-extrabold text-violet-700">{fa.primary}{fa.secondary ? <span className="text-sm font-bold text-violet-400"> · {fa.secondary}</span> : null}</h3>
            {fa.vibe && <p className="text-xs text-violet-800 mt-0.5">{fa.vibe}</p>}
            {fa.essence && <p className="text-[11px] text-violet-700 italic mt-1">{fa.essence}</p>}
          </div>
        )}
        {kb.type && (
          <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl border-2 border-amber-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-600 font-extrabold">Kibbe-typ</div>
            <h3 className="text-xl font-extrabold text-amber-800">{kb.type}</h3>
            {kb.explanation && <p className="text-xs text-amber-900 mt-0.5">{kb.explanation}</p>}
            {(kb.lines || kb.proportions) && (
              <div className="grid grid-cols-2 gap-2 mt-1.5 text-[11px]">
                {kb.lines && <div><b className="text-amber-700">Linjer:</b> <span className="text-gray-700">{kb.lines}</span></div>}
                {kb.proportions && <div><b className="text-amber-700">Proportioner:</b> <span className="text-gray-700">{kb.proportions}</span></div>}
              </div>
            )}
          </div>
        )}
        {cp.season && (
          <div>
            <h4 className="text-xs font-extrabold text-rose-700 uppercase">Färgsäsong — {cp.season} {cp.undertone && <span className="opacity-60 normal-case">· {cp.undertone}</span>}</h4>
            {(cp.best || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-emerald-700 mt-1">Dina färger ✓</div>
                <div className="flex flex-wrap gap-1 mt-0.5">{cp.best.map((c, i) => <span key={i} className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{c}</span>)}</div>
              </div>
            )}
            {(cp.archetype_palette || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-violet-700 mt-1.5">Arketypens palett 🌙</div>
                <div className="flex flex-wrap gap-1 mt-0.5">{cp.archetype_palette.map((c, i) => <span key={i} className="text-xs bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full font-bold">{c}</span>)}</div>
              </div>
            )}
            {(cp.avoid || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold text-rose-700 mt-1.5">Undvik ✗</div>
                <div className="flex flex-wrap gap-1 mt-0.5">{cp.avoid.map((c, i) => <span key={i} className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full font-bold">{c}</span>)}</div>
              </div>
            )}
          </div>
        )}
        {(result.shopping_list || []).length > 0 && (() => {
          const totalItems = result.shopping_list.reduce((n, cat) => n + (cat.items || []).length, 0);
          const doneCount = Object.entries(checked).filter(([, v]) => v).length;
          return (
            <div>
              <h4 className="text-xs font-extrabold text-pink-700 uppercase flex items-center gap-1 justify-between">
                <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Shoppinglista</span>
                <span className="text-[10px] bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-full normal-case" data-testid="lou-checklist-progress">{doneCount}/{totalItems} köpta</span>
              </h4>
              <div className="space-y-1.5 mt-1">
                {result.shopping_list.map((cat, i) => (
                  <div key={i} className="bg-pink-50 border border-pink-100 rounded-xl px-2.5 py-1.5">
                    <div className="text-[11px] font-extrabold text-pink-800">{cat.category}</div>
                    <ul className="text-[11px] text-gray-700 space-y-0.5 mt-0.5">
                      {(cat.items || []).map((it, j) => {
                        const key = `${cat.category}::${it}`;
                        const isChecked = !!checked[key];
                        return (
                          <li key={j}>
                            <label className="flex items-start gap-1.5 cursor-pointer hover:bg-pink-100/60 rounded px-1 py-0.5 -mx-1">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleCheck(cat.category, it)}
                                data-testid={`lou-check-${i}-${j}`}
                                className="mt-0.5 accent-pink-500 flex-shrink-0"
                              />
                              <span className={isChecked ? 'line-through opacity-60' : ''}>{it}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {(result.avoid_list || []).length > 0 && (
          <div>
            <h4 className="text-xs font-extrabold text-rose-700 uppercase">Undvik silhuetter ✗</h4>
            <ul className="text-[11px] text-gray-700 list-disc list-inside mt-1 space-y-0.5">
              {result.avoid_list.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
        {(result.outfit_examples || []).length > 0 && (
          <div>
            <h4 className="text-xs font-extrabold text-violet-700 uppercase">Outfit-exempel ✨</h4>
            <ol className="text-[11px] text-gray-700 list-decimal list-inside mt-1 space-y-1">
              {result.outfit_examples.map((o, i) => <li key={i}>{o}</li>)}
            </ol>
          </div>
        )}
        <button onClick={() => setResult(null)} data-testid="lou-style-redo" className="w-full text-xs text-rose-600 underline">Gör om analys med nya bilder</button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="lou-style-form">
      <div className="bg-rose-50 border border-rose-200 rounded-2xl px-3 py-2 text-[11px] text-rose-800">
        💡 <b>Tips för bra analys:</b> selfie i naturligt ljus utan smink + 1-3 helkroppsbilder i tightsittande kläder. Lou läser av faktiska färger & proportioner — ingen bild sparas.
      </div>

      <div>
        <label className="text-xs font-bold text-rose-700 flex items-center gap-1"><Camera className="w-3 h-3" /> Selfie (ansikte, naturligt ljus)</label>
        <label className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-rose-300 cursor-pointer hover:bg-rose-50 text-xs text-rose-700">
          <Upload className="w-3.5 h-3.5" />
          <span>{facePhoto ? 'Byt selfie' : 'Välj selfie'}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickFace} data-testid="lou-style-face" className="hidden" />
        </label>
        {facePhoto && <img src={facePhoto.preview} alt="" className="w-20 h-20 object-cover rounded-xl border-2 border-rose-200 mt-1.5" />}
      </div>

      <div>
        <label className="text-xs font-bold text-rose-700 flex items-center gap-1"><Camera className="w-3 h-3" /> Helkroppsbilder (1-3 st)</label>
        <label className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-rose-300 cursor-pointer hover:bg-rose-50 text-xs text-rose-700">
          <Upload className="w-3.5 h-3.5" />
          <span>{bodyPhotos.length > 0 ? `${bodyPhotos.length} bild${bodyPhotos.length > 1 ? 'er' : ''} valda — byt` : 'Välj helkroppsbilder'}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={pickBody} data-testid="lou-style-body" className="hidden" />
        </label>
        {bodyPhotos.length > 0 && (
          <div className="flex gap-1.5 mt-1.5">
            {bodyPhotos.map((b, i) => <img key={i} src={b.preview} alt="" className="w-16 h-20 object-cover rounded-xl border-2 border-rose-200" />)}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-bold text-rose-700">Något extra Lou bör veta? (frivilligt)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="t.ex. 'känner mig fel klädd i kjolar', 'gillar mörkt' …"
          data-testid="lou-style-notes"
          className="w-full px-3 py-2 rounded-xl border border-rose-200 text-sm resize-none"
        />
      </div>

      <button
        onClick={run}
        disabled={busy || (!facePhoto && bodyPhotos.length === 0)}
        data-testid="lou-style-analyze"
        className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-500 to-violet-500 hover:from-pink-600 hover:via-rose-600 hover:to-violet-600 text-white font-extrabold text-sm shadow disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Sparkles className="w-4 h-4" /> Analysera min stil — arketyp + Kibbe
      </button>
    </div>
  );
}

// ============== SPECIAL MODALS ==============
function LouAnalysis({ onClose }) {
  const [tab, setTab] = useState('color');   // 'color' | 'style'
  const [form, setForm] = useState({ hair_natural: '', eyes: '', skin_undertone: '', veins: '', burns_or_tans: '', extra: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [styleExisting, setStyleExisting] = useState(null);

  useEffect(() => {
    axios.get(`${API}/experts/lou/color-analysis`, { withCredentials: true })
      .then((r) => { if (r.data?.has_analysis) setResult(r.data); })
      .catch(() => {});
    axios.get(`${API}/experts/lou/style-analysis`, { withCredentials: true })
      .then((r) => { if (r.data?.has_analysis) setStyleExisting(r.data); })
      .catch(() => {});
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/experts/lou/color-analysis`, form, { withCredentials: true });
      setResult(r.data);
      toast.success('Analys klar 💄');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-pink-400 to-rose-500 px-5 py-3 text-white flex justify-between items-center">
          <h3 className="font-extrabold flex items-center gap-2"><Palette className="w-4 h-4" /> Lou — färg & stil</h3>
          <button onClick={onClose} data-testid="lou-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex border-b border-rose-100">
          <button
            onClick={() => setTab('color')}
            data-testid="lou-tab-color"
            className={`flex-1 px-3 py-2 text-xs font-extrabold uppercase tracking-wide ${tab === 'color' ? 'text-rose-700 border-b-2 border-rose-500 bg-rose-50' : 'text-gray-400 hover:text-rose-500'}`}
          >🎨 Färganalys (text)</button>
          <button
            onClick={() => setTab('style')}
            data-testid="lou-tab-style"
            className={`flex-1 px-3 py-2 text-xs font-extrabold uppercase tracking-wide ${tab === 'style' ? 'text-rose-700 border-b-2 border-rose-500 bg-rose-50' : 'text-gray-400 hover:text-rose-500'}`}
          >📸 Stil + arketyp</button>
        </div>
        <div className="p-5 space-y-3">
          {tab === 'style' && <LouStyleAnalysis existing={styleExisting} onClose={onClose} />}
          {tab === 'color' && !result && (
            <>
              {[
                ['hair_natural', 'Naturlig hårfärg', 't.ex. mörkblont, askbrunt'],
                ['eyes', 'Ögonfärg', 't.ex. blå med grön ring'],
                ['skin_undertone', 'Hudundertyp', 'varm / kall / neutral'],
                ['veins', 'Vener i armvecket', 'blå-lila / grön / blandat'],
                ['burns_or_tans', 'Sol', 'bränns lätt / blir brun snabbt'],
                ['extra', 'Annat', 'frivilligt'],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="text-xs font-bold text-rose-700">{label}</label>
                  <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} data-testid={`lou-${k}`} className="w-full px-3 py-2 rounded-xl border border-rose-200 text-sm" />
                </div>
              ))}
              <button onClick={run} disabled={busy} data-testid="lou-analyze" className="w-full py-2.5 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-extrabold disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Analysera 💋'}
              </button>
            </>
          )}
          {result && (
            <div className={`space-y-3 ${tab !== 'color' ? 'hidden' : ''}`} data-testid="lou-result">
              <div className="text-center">
                <div className="text-xs uppercase tracking-widest text-rose-500 font-bold">Din säsong</div>
                <h2 className="text-2xl font-extrabold text-rose-700">{result.season}</h2>
                <p className="text-xs text-gray-600 italic mt-1">{result.summary}</p>
              </div>
              <div>
                <h4 className="text-xs font-extrabold text-emerald-700 uppercase">Dina färger ✓</h4>
                <div className="flex flex-wrap gap-1 mt-1">{(result.best_colors || []).map((c, i) => <span key={i} className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">{c}</span>)}</div>
              </div>
              <div>
                <h4 className="text-xs font-extrabold text-rose-700 uppercase">Undvik ✗</h4>
                <div className="flex flex-wrap gap-1 mt-1">{(result.avoid_colors || []).map((c, i) => <span key={i} className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full font-bold">{c}</span>)}</div>
              </div>
              {result.makeup && (
                <div>
                  <h4 className="text-xs font-extrabold text-pink-700 uppercase">Smink</h4>
                  <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                    {result.makeup.lipstick && <li><b>Läppstift:</b> {Array.isArray(result.makeup.lipstick) ? result.makeup.lipstick.join(', ') : result.makeup.lipstick}</li>}
                    {result.makeup.eyeshadow && <li><b>Ögonskugga:</b> {Array.isArray(result.makeup.eyeshadow) ? result.makeup.eyeshadow.join(', ') : result.makeup.eyeshadow}</li>}
                    {result.makeup.mascara && <li><b>Mascara:</b> {result.makeup.mascara}</li>}
                    {result.makeup.blush && <li><b>Rouge:</b> {result.makeup.blush}</li>}
                  </ul>
                </div>
              )}
              {result.style_tips?.length > 0 && (
                <div>
                  <h4 className="text-xs font-extrabold text-violet-700 uppercase">Stil-tips</h4>
                  <ul className="text-xs text-gray-700 mt-1 space-y-0.5 list-disc list-inside">{result.style_tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
                </div>
              )}
              <button onClick={() => setResult(null)} className="w-full text-xs text-rose-600 underline">Gör om analys</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GenericJsonModal({ title, color, icon: Icon, endpoint, fields, onClose, renderResult }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}${endpoint}`, form, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className={`bg-gradient-to-r ${color} px-5 py-3 text-white flex justify-between items-center`}>
          <h3 className="font-extrabold flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {!result && (
            <>
              {fields.map((f) => (
                <div key={f.id}>
                  <label className="text-xs font-bold text-gray-700">{f.label}</label>
                  {f.type === 'select' ? (
                    <select value={form[f.id] || ''} onChange={(e) => setForm({ ...form, [f.id]: e.target.value })} data-testid={`special-${f.id}`} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white">
                      <option value="">— välj —</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === 'number' ? (
                    <input type="number" value={form[f.id] || ''} onChange={(e) => setForm({ ...form, [f.id]: Number(e.target.value) })} placeholder={f.placeholder} data-testid={`special-${f.id}`} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                  ) : (
                    <input value={form[f.id] || ''} onChange={(e) => setForm({ ...form, [f.id]: e.target.value })} placeholder={f.placeholder} data-testid={`special-${f.id}`} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                  )}
                </div>
              ))}
              <button onClick={run} disabled={busy} data-testid="special-go" className={`w-full py-2.5 rounded-2xl bg-gradient-to-r ${color} text-white font-extrabold disabled:opacity-50`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Kör ✨'}
              </button>
            </>
          )}
          {result && (
            <div className="space-y-2" data-testid="special-result">
              {renderResult(result)}
              <button onClick={() => setResult(null)} className="w-full text-xs underline opacity-70">Gör en till</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============== MAIN HUB ==============
export default function ExpertsHub() {
  const [experts, setExperts] = useState([]);
  const [active, setActive] = useState(null);
  const [special, setSpecial] = useState(null);   // 'lou' | 'june' | 'stella' | 'elin' | null

  const loadAll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/experts/`, { withCredentials: true });
      setExperts(r.data?.items || []);
    } catch (e) {
      toast.error('Kunde inte ladda experter');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-4" data-testid="experts-hub">
      {!active && (
        <>
          <div className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 rounded-3xl p-5 text-white shadow">
            <h2 className="text-2xl font-extrabold flex items-center gap-2"><Sparkles className="w-5 h-5" /> Ditt expert-team</h2>
            <p className="text-sm opacity-90 italic">Fem proffs för olika delar av livet. Tryck på en för att prata.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {experts.map((e) => (
              <button
                key={e.id}
                onClick={() => setActive(e)}
                data-testid={`expert-card-${e.id}`}
                className={`text-left bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition overflow-hidden group`}
              >
                <div className={`bg-gradient-to-r ${e.color} px-4 py-3 flex items-center gap-3`}>
                  <div className="w-12 h-12 rounded-full bg-white/30 overflow-hidden flex items-center justify-center text-xl border-2 border-white/50">
                    {e.portrait_b64 ? (
                      <img src={`data:${e.portrait_mime || 'image/png'};base64,${e.portrait_b64}`} alt={e.name} className="w-full h-full object-cover" />
                    ) : (
                      <span>{e.emoji}</span>
                    )}
                  </div>
                  <div className="text-white">
                    <div className="font-extrabold">{e.name}</div>
                    <div className="text-[11px] opacity-90">{e.title}</div>
                  </div>
                </div>
                <div className="p-3 text-xs text-gray-600 italic">Tryck för att prata med {e.name} →</div>
              </button>
            ))}
          </div>
        </>
      )}

      {active && (
        <ExpertChat
          expert={active}
          onBack={() => { setActive(null); loadAll(); }}
          onOpenSpecial={(k) => setSpecial(k)}
        />
      )}

      {special === 'lou' && <LouAnalysis onClose={() => setSpecial(null)} />}
      {special === 'june' && (
        <GenericJsonModal
          title="Bujo-mall från June"
          color="from-amber-400 to-orange-500"
          icon={BookOpen}
          endpoint="/experts/june/template"
          fields={[
            { id: 'purpose', label: 'Vad ska mallen användas till?', placeholder: 'veckokoll, mood tracker, morgonrutin…' },
            { id: 'style', label: 'Stil', type: 'select', options: ['minimalistisk', 'färgglad', 'boho'] },
            { id: 'space', label: 'Yta', type: 'select', options: ['halv sida', 'helsida', 'dubbeluppslag'] },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              <h3 className="text-lg font-extrabold text-amber-700">{r.name}</h3>
              {r.ascii_layout && <pre className="text-[10px] bg-amber-50 p-2 rounded-xl border border-amber-200 whitespace-pre overflow-x-auto">{r.ascii_layout}</pre>}
              {(r.sections || []).length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-amber-700 uppercase">Sektioner</h4>
                  <ul className="text-xs text-gray-700 space-y-1 mt-1">{r.sections.map((s, i) => <li key={i}><b>{s.title}</b> — {s.what}</li>)}</ul>
                </div>
              )}
              {(r.materials || []).length > 0 && <p className="text-xs"><b>Material:</b> {r.materials.join(', ')}</p>}
              {(r.step_by_step || []).length > 0 && (
                <ol className="text-xs text-gray-700 list-decimal list-inside space-y-0.5">{r.step_by_step.map((s, i) => <li key={i}>{s}</li>)}</ol>
              )}
              {r.adhd_tip && <p className="text-xs italic text-amber-800 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">💡 {r.adhd_tip}</p>}
            </>
          )}
        />
      )}
      {special === 'stella' && (
        <GenericJsonModal
          title="Städ-plan från Stella"
          color="from-teal-400 to-emerald-500"
          icon={Brush}
          endpoint="/experts/stella/plan"
          fields={[
            { id: 'area', label: 'Område', placeholder: 'köket, hallen, barnens rum…' },
            { id: 'time_min', label: 'Tid (minuter)', type: 'number', placeholder: '15' },
            { id: 'energy', label: 'Energinivå', type: 'select', options: ['låg', 'medel', 'hög'] },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              <h3 className="text-lg font-extrabold text-teal-700">{r.name}</h3>
              <p className="text-xs italic text-teal-800">{r.intro}</p>
              {(r.steps || []).map((s, i) => (
                <div key={i} className="bg-teal-50 rounded-xl p-2 border border-teal-100">
                  <div className="font-bold text-sm text-teal-800">{i + 1}. {s.title} {s.minutes && <span className="text-xs opacity-70">({s.minutes} min)</span>}</div>
                  {s.detail && <div className="text-xs text-gray-700">{s.detail}</div>}
                </div>
              ))}
              {r.timer_hack && <p className="text-xs"><b>Timer-hack:</b> {r.timer_hack}</p>}
              {r.reward && <p className="text-xs italic text-emerald-700">🎁 {r.reward}</p>}
            </>
          )}
        />
      )}
      {special === 'fia' && (
        <GenericJsonModal
          title="Lös låsning — Dr. Fia"
          color="from-sky-400 to-cyan-500"
          icon={Bone}
          endpoint="/experts/fia/release"
          fields={[
            { id: 'area', label: 'Område', type: 'select', options: ['rygg', 'nacke', 'axlar', 'höft', 'käke', 'handleder', 'helkropp'] },
            { id: 'intensity', label: 'Intensitet', type: 'select', options: ['mjuk', 'medel', 'djup'] },
            { id: 'duration_min', label: 'Tid (minuter)', type: 'number', placeholder: '5' },
            { id: 'notes', label: 'Detaljer (frivilligt)', placeholder: 't.ex. låsning i nedre ländrygg, gör ont vid framåtböj' },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              <h3 className="text-lg font-extrabold text-sky-700">{r.title || 'Rutin'}</h3>
              {r.intro && <p className="text-xs italic text-sky-800">{r.intro}</p>}
              {r.disclaimer && (
                <div className="text-[11px] bg-amber-50 text-amber-900 px-3 py-2 rounded-xl border border-amber-200 flex gap-2 items-start">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{r.disclaimer}</span>
                </div>
              )}
              {Array.isArray(r.warning_signs) && r.warning_signs.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-extrabold text-rose-700 uppercase">Sök vård vid</h4>
                  <ul className="text-xs text-rose-800 list-disc list-inside mt-0.5">
                    {r.warning_signs.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {(r.steps || []).map((s, i) => (
                <div key={i} className="bg-sky-50 rounded-xl p-2 border border-sky-100 space-y-1">
                  <div className="font-bold text-sm text-sky-900">
                    {i + 1}. {s.name}
                    {s.duration_sec && <span className="text-[10px] opacity-70 ml-1">({s.duration_sec}s)</span>}
                    {s.reps && <span className="text-[10px] opacity-70 ml-1">· {s.reps}</span>}
                  </div>
                  {s.how_to && <div className="text-xs text-gray-700 whitespace-pre-wrap">{s.how_to}</div>}
                  {s.breath_cue && <div className="text-[11px] text-cyan-700 italic">🌬️ {s.breath_cue}</div>}
                  {s.safety && <div className="text-[10px] text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded">⚠ {s.safety}</div>}
                </div>
              ))}
              {r.outro && <p className="text-xs italic text-sky-700">{r.outro}</p>}
              <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-3 py-2 text-[11px] text-cyan-800">
                💡 Vill du att <b>Nova</b> guidar dig med röst-coaching steg för steg? Gå till <b>PT Nova</b> → knappen &quot;Lös låsning 🦴&quot;.
              </div>
            </>
          )}
        />
      )}
      {special === 'elin' && (
        <GenericJsonModal
          title="Strategi från Dr. Elin"
          color="from-violet-400 to-indigo-500"
          icon={Brain}
          endpoint="/experts/elin/strategy"
          fields={[
            { id: 'feeling', label: 'Vad känner du nu?', placeholder: 'ångest, ledsen, rastlös, stressad…' },
            { id: 'context', label: 'Kontext (frivilligt)', placeholder: 'jobbigt med chefen, sömnlös…' },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              {r.validation && <p className="text-sm bg-violet-50 border border-violet-200 rounded-xl px-3 py-2 italic text-violet-800">💜 {r.validation}</p>}
              {r.strategy && <div><h4 className="text-xs font-extrabold text-violet-700 uppercase">Strategi</h4><p className="text-sm font-bold">{r.strategy}</p></div>}
              {(r.steps || []).length > 0 && (
                <ol className="text-sm text-gray-700 list-decimal list-inside space-y-1 bg-white rounded-xl p-2 border border-violet-100">{r.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              )}
              {r.why_it_helps && <p className="text-xs italic text-gray-600">{r.why_it_helps}</p>}
              {r.emergency_note && <p className="text-xs bg-rose-50 text-rose-800 px-3 py-2 rounded-xl border border-rose-200">⚠️ {r.emergency_note}</p>}
            </>
          )}
        />
      )}
      {special === 'farmor' && (
        <GenericJsonModal
          title="Husmorstips från Farmor Elsa"
          color="from-rose-300 to-amber-400"
          icon={Home}
          endpoint="/experts/farmor/tips"
          fields={[
            { id: 'task', label: 'Vad ska du göra?', placeholder: 't.ex. torka av spisen, få bort kalk i badrummet, ta bort rödvinsfläck' },
            { id: 'area', label: 'Område (frivilligt)', placeholder: 'köket, badrummet, tvätten, vardagsrum…' },
            { id: 'time_min', label: 'Tid (minuter)', type: 'number', placeholder: '15' },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              {r.intro && (
                <p className="text-sm bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 rounded-xl px-3 py-2 italic text-rose-800">
                  👵 {r.intro}
                </p>
              )}
              {(r.materials || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5">
                  <div className="text-[10px] font-extrabold text-amber-800 uppercase tracking-wide mb-1">🧺 Ta fram</div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.materials.map((m, i) => (
                      <span key={i} className="text-xs bg-white px-2 py-0.5 rounded-full border border-amber-300 text-amber-800">{m}</span>
                    ))}
                  </div>
                </div>
              )}
              {(r.steps || []).map((s, i) => (
                <div key={i} className="relative bg-white rounded-xl p-3 border-2 border-rose-100 pl-11">
                  <div className="absolute left-2.5 top-2.5 w-6 h-6 rounded-full bg-gradient-to-br from-rose-300 to-amber-400 text-white flex items-center justify-center text-xs font-extrabold">
                    {s.order || i + 1}
                  </div>
                  <div className="font-bold text-sm text-rose-800 flex items-center gap-2 flex-wrap">
                    {s.title}
                    {s.minutes && (
                      <span className="text-[10px] font-normal bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full">
                        ⏱ {s.minutes} min
                      </span>
                    )}
                  </div>
                  {s.tip && <div className="text-xs text-gray-700 mt-1 leading-relaxed">{s.tip}</div>}
                  {s.wait_while && (
                    <div className="text-[11px] mt-2 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-amber-800">
                      💡 <b>Medan det verkar:</b> {s.wait_while}
                    </div>
                  )}
                </div>
              ))}
              {r.smart_sequence && (
                <p className="text-xs bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-rose-800">
                  <b>✨ Farmors knep:</b> {r.smart_sequence}
                </p>
              )}
              {r.farmors_kloka_ord && (
                <p className="text-xs italic text-rose-900 border-t border-rose-100 pt-2">
                  👵💕 {r.farmors_kloka_ord}
                </p>
              )}
            </>
          )}
        />
      )}
      {special === 'hedda' && (
        <GenericJsonModal
          title="Måltidsförslag från Hedda"
          color="from-lime-400 to-emerald-500"
          icon={Salad}
          endpoint="/experts/hedda/meal"
          fields={[
            { id: 'hunger', label: 'Hur hungrig är du?', type: 'select', options: ['hangry', 'medel', 'lätt hunger', 'bara mellanmål'] },
            { id: 'energy', label: 'Energinivå', type: 'select', options: ['utmattad', 'låg', 'medel', 'hög'] },
            { id: 'time_min', label: 'Tid (minuter)', type: 'number', placeholder: '10' },
            { id: 'meal_type', label: 'Typ av måltid', type: 'select', options: ['vad_som', 'frukost', 'lunch', 'middag', 'mellanmål'] },
            { id: 'fridge', label: 'Vad har du hemma? (frivilligt)', placeholder: 'keso, ägg, gurka, bröd, ost, havregryn…' },
            { id: 'notes', label: 'Övrigt (frivilligt)', placeholder: 'medicin snart, PMS-sug efter salt, ingen ork att laga…' },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              {r.intro && (
                <p className="text-sm bg-gradient-to-r from-lime-50 to-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 italic text-emerald-800">
                  🥗 {r.intro}
                </p>
              )}
              {r.blood_sugar_note && (
                <p className="text-[11px] bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-1.5 text-emerald-700">
                  🧠 {r.blood_sugar_note}
                </p>
              )}
              {(r.meals || []).map((m, i) => (
                <div key={i} className="bg-white rounded-xl p-3 border-2 border-emerald-100 space-y-1.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-bold text-sm text-emerald-900">{i + 1}. {m.name}</div>
                    <div className="flex gap-1 flex-wrap">
                      {m.prep_min != null && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">⏱ {m.prep_min} min</span>
                      )}
                      {m.protein_grams != null && (
                        <span className="text-[10px] bg-lime-100 text-lime-800 px-1.5 py-0.5 rounded-full">💪 {m.protein_grams}g protein</span>
                      )}
                    </div>
                  </div>
                  {m.why_adhd && (
                    <div className="text-[11px] italic text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">
                      🧠 {m.why_adhd}
                    </div>
                  )}
                  {(m.ingredients || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {m.ingredients.map((ing, j) => (
                        <span key={j} className="text-[11px] bg-lime-50 border border-lime-200 rounded-full px-2 py-0.5 text-lime-800">{ing}</span>
                      ))}
                    </div>
                  )}
                  {(m.steps || []).length > 0 && (
                    <ol className="text-xs text-gray-700 list-decimal list-inside space-y-0.5">
                      {m.steps.map((s, j) => <li key={j}>{s}</li>)}
                    </ol>
                  )}
                  {(m.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {m.tags.map((t, j) => (
                        <span key={j} className="text-[10px] bg-white border border-emerald-200 rounded-full px-1.5 py-0.5 text-emerald-700">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {r.adhd_tip && (
                <p className="text-xs italic text-emerald-900 bg-lime-50 border border-lime-200 rounded-xl px-3 py-2">
                  💡 <b>ADHD-hack:</b> {r.adhd_tip}
                </p>
              )}
              {r.if_no_energy && (
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  🌱 <b>Om orken är helt slut:</b> {r.if_no_energy}
                </p>
              )}
            </>
          )}
        />
      )}
      {special === 'astrid' && (
        <GenericJsonModal
          title="Recept från Astrid"
          color="from-rose-300 to-pink-400"
          icon={ChefHat}
          endpoint="/experts/astrid/recipe"
          fields={[
            { id: 'dish_type', label: 'Typ av rätt', type: 'select', options: ['vad_som', 'tårta', 'kaka', 'bröd', 'middag', 'soppa', 'sås', 'matlåda'] },
            { id: 'occasion', label: 'Tillfälle', type: 'select', options: ['vardagsfika', 'födelsedagstårta', 'imponera', 'trösta', 'barnkalas', 'middag', 'lunch', 'matlåda'] },
            { id: 'difficulty', label: 'Svårighetsgrad', type: 'select', options: ['nybörjare', 'medel', 'utmaning'] },
            { id: 'time_min', label: 'Tid (minuter)', type: 'number', placeholder: '60' },
            { id: 'servings', label: 'Portioner', type: 'number', placeholder: '8' },
            { id: 'dietary', label: 'Allergener / kost (frivilligt)', placeholder: 'glutenfri, mjölkfri, vegan...' },
            { id: 'craving', label: 'Sug efter / önskemål', placeholder: 'choklad, citron, kanel, kyckling...' },
          ]}
          onClose={() => setSpecial(null)}
          renderResult={(r) => (
            <>
              {r.intro && (
                <p className="text-sm bg-gradient-to-r from-rose-50 to-pink-50 border border-pink-200 rounded-xl px-3 py-2 italic text-rose-900">
                  🎂 {r.intro}
                </p>
              )}
              {r.recipe_name && (
                <div className="text-center py-2">
                  <h3 className="text-xl font-['Fredoka'] font-bold text-rose-900">{r.recipe_name}</h3>
                  {r.why_this && <p className="text-[11px] italic opacity-70 mt-0.5">{r.why_this}</p>}
                </div>
              )}
              <div className="grid grid-cols-3 gap-1.5">
                {r.total_time_min != null && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-center">
                    <div className="text-[9px] uppercase font-bold opacity-60">Total</div>
                    <div className="text-sm font-bold">⏱ {r.total_time_min} min</div>
                  </div>
                )}
                {r.active_time_min != null && (
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-2 text-center">
                    <div className="text-[9px] uppercase font-bold opacity-60">Aktiv</div>
                    <div className="text-sm font-bold">👐 {r.active_time_min} min</div>
                  </div>
                )}
                {r.pause_points_min != null && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-center">
                    <div className="text-[9px] uppercase font-bold opacity-60">Pauser</div>
                    <div className="text-sm font-bold">😌 {r.pause_points_min} min</div>
                  </div>
                )}
              </div>
              {(r.mise_en_place || []).length > 0 && (
                <div className="bg-white border-2 border-rose-100 rounded-xl p-3">
                  <div className="text-[10px] font-bold uppercase text-rose-700 mb-1">🧺 Mise-en-place (mät upp först!)</div>
                  <ul className="text-xs space-y-0.5">
                    {r.mise_en_place.map((m, i) => <li key={i}>• {m}</li>)}
                  </ul>
                </div>
              )}
              {(r.ingredients || []).length > 0 && (
                <div className="bg-white border-2 border-pink-100 rounded-xl p-3">
                  <div className="text-[10px] font-bold uppercase text-pink-700 mb-1">📋 Ingredienser {r.servings ? `(${r.servings} port)` : ''}</div>
                  <ul className="text-xs space-y-0.5">
                    {r.ingredients.map((ing, i) => (
                      <li key={i} className="flex justify-between border-b border-pink-50 py-0.5">
                        <span>{ing.item}</span>
                        <span className="font-mono text-pink-800">{ing.amount}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(r.equipment || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {r.equipment.map((e, i) => (
                    <span key={i} className="text-[10px] bg-white border border-rose-200 rounded-full px-2 py-0.5">🥣 {e}</span>
                  ))}
                </div>
              )}
              {(r.steps || []).length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-bold uppercase text-rose-700 px-1">👣 Steg för steg</div>
                  {r.steps.map((s, i) => (
                    <div key={i} className={`rounded-xl p-2.5 border ${s.can_pause ? 'bg-purple-50 border-purple-200' : 'bg-white border-rose-100'}`}>
                      <div className="flex items-baseline gap-2">
                        <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 font-bold">{s.n}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold">{s.title}</div>
                          <div className="text-[11px] text-gray-800 leading-relaxed mt-0.5">{s.instruction}</div>
                          {s.time_min != null && (
                            <div className="text-[10px] opacity-60 mt-1">⏱ {s.time_min} min</div>
                          )}
                          {s.can_pause && s.pause_note && (
                            <div className="text-[10px] mt-1 italic bg-purple-100 text-purple-900 px-2 py-1 rounded-lg">
                              😌 <b>PAUS-punkt:</b> {s.pause_note}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {r.finish && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs">
                  <div className="text-[10px] font-bold uppercase text-amber-700 mb-1">🌟 Avslutning</div>
                  {r.finish}
                </div>
              )}
              {(r.adhd_tips || []).length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <div className="text-[10px] font-bold uppercase text-emerald-700 mb-1">🧠 ADHD-hack</div>
                  <ul className="text-xs space-y-0.5">
                    {r.adhd_tips.map((t, i) => <li key={i}>💡 {t}</li>)}
                  </ul>
                </div>
              )}
              {r.if_it_goes_wrong && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs">
                  <div className="text-[10px] font-bold uppercase text-orange-700 mb-1">🚨 Om något går fel</div>
                  <div><b>{r.if_it_goes_wrong.scenario}</b></div>
                  <div className="mt-0.5 italic">→ {r.if_it_goes_wrong.rescue}</div>
                </div>
              )}
              {r.leftovers && (
                <p className="text-[11px] italic text-gray-700 border-t border-rose-100 pt-2">
                  📦 <b>Rester:</b> {r.leftovers}
                </p>
              )}
              <SaveRecipeButton recipe={r} />
            </>
          )}
        />
      )}
    </div>
  );
}

// ============== SAVE / FAVORITES ==============
function SaveRecipeButton({ recipe }) {
  const [saved, setSaved] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [customName, setCustomName] = React.useState(recipe?.recipe_name || '');

  const save = async () => {
    if (saved || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/experts/astrid/favorites`, {
        recipe,
        custom_name: customName,
      }, { withCredentials: true });
      setSaved(true);
      toast.success('💛 Sparat i dina favoriter');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  if (saved) {
    return (
      <div className="bg-rose-50 border-2 border-rose-300 rounded-xl px-3 py-2 text-xs text-rose-900 flex items-center gap-2" data-testid="astrid-save-confirm">
        <Heart className="w-4 h-4 fill-rose-500 text-rose-500" /> Sparat! Hitta det senare via Favoriter-knappen. ✨
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 border-t border-rose-100 pt-3">
      <input
        value={customName}
        onChange={(e) => setCustomName(e.target.value)}
        className="neo-input flex-1 text-xs"
        placeholder="Namn på receptet"
        data-testid="astrid-save-name"
      />
      <button
        onClick={save}
        disabled={busy || !customName.trim()}
        className="neo-btn px-3 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50"
        data-testid="astrid-save-btn"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />} Spara
      </button>
    </div>
  );
}

function AstridFavoritesModal({ onClose }) {
  const [items, setItems] = React.useState(null);
  const [openId, setOpenId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/experts/astrid/favorites`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { toast.error('Kunde inte ladda favoriter'); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Ta bort detta sparade recept?')) return;
    try {
      await axios.delete(`${API}/experts/astrid/favorites/${id}`, { withCredentials: true });
      toast.success('Borttaget');
      load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const active = items?.find((x) => x.id === openId);

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="astrid-favorites-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-rose-300 to-pink-400 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bookmark className="w-5 h-5" />
            <h2 className="text-xl font-bold font-['Fredoka']">Mina favoritrecept</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-full bg-white/20 hover:bg-white/30" data-testid="astrid-favorites-close" aria-label="Stäng">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {items === null && <div className="text-center text-xs opacity-50 py-6">Laddar...</div>}
          {items !== null && items.length === 0 && (
            <div className="text-center py-10 space-y-2">
              <div className="text-4xl">📖</div>
              <p className="text-sm opacity-70 italic">Inga sparade recept än.</p>
              <p className="text-[11px] opacity-50">När Astrid ger dig ett recept — klicka &ldquo;Spara&rdquo; längst ner så hamnar det här. 💛</p>
            </div>
          )}
          {active ? (
            <div data-testid="astrid-favorite-detail">
              <button onClick={() => setOpenId(null)} className="text-xs font-bold text-rose-700 mb-3 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Tillbaka till listan
              </button>
              <h3 className="text-xl font-['Fredoka'] font-bold text-rose-900 mb-2">{active.name}</h3>
              {active.notes && (
                <p className="text-[11px] italic bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1 mb-3">📝 {active.notes}</p>
              )}
              <div className="space-y-3">
                {(active.recipe?.ingredients || []).length > 0 && (
                  <div className="bg-pink-50 border-2 border-pink-200 rounded-xl p-3">
                    <div className="text-[10px] font-bold uppercase text-pink-700 mb-1">📋 Ingredienser</div>
                    <ul className="text-xs space-y-0.5">
                      {active.recipe.ingredients.map((ing, i) => (
                        <li key={i} className="flex justify-between border-b border-pink-100 py-0.5">
                          <span>{ing.item}</span>
                          <span className="font-mono text-pink-800">{ing.amount}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(active.recipe?.steps || []).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase text-rose-700 px-1">👣 Steg</div>
                    {active.recipe.steps.map((s, i) => (
                      <div key={i} className={`rounded-xl p-2.5 border ${s.can_pause ? 'bg-purple-50 border-purple-200' : 'bg-white border-rose-100'}`}>
                        <div className="flex items-baseline gap-2">
                          <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 font-bold">{s.n}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold">{s.title}</div>
                            <div className="text-[11px] text-gray-800 leading-relaxed mt-0.5">{s.instruction}</div>
                            {s.can_pause && s.pause_note && (
                              <div className="text-[10px] mt-1 italic bg-purple-100 text-purple-900 px-2 py-1 rounded-lg">
                                😌 <b>PAUS:</b> {s.pause_note}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {active.recipe?.finish && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs">
                    🌟 {active.recipe.finish}
                  </div>
                )}
                <button
                  onClick={() => del(active.id)}
                  className="w-full py-2 rounded-full bg-red-50 text-red-700 text-xs font-bold flex items-center justify-center gap-1"
                  data-testid={`astrid-favorite-delete-${active.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Ta bort från favoriter
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="astrid-favorites-list">
              {(items || []).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setOpenId(f.id)}
                  className="w-full text-left neo-card p-3 bg-white hover:bg-rose-50 flex items-center gap-3"
                  data-testid={`astrid-favorite-${f.id}`}
                >
                  <div className="text-2xl">🎂</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{f.name}</div>
                    <div className="text-[10px] opacity-60">
                      {f.recipe?.total_time_min ? `${f.recipe.total_time_min} min` : ''}
                      {f.recipe?.difficulty ? ` · ${f.recipe.difficulty}` : ''}
                      {f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString('sv-SE')}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============== FARMOR LIBRARY MODAL ==============
function FarmorLibraryModal({ onClose, items, setItems }) {
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState('');
  const [detail, setDetail] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/experts/farmor/library`, { withCredentials: true });
        setItems(data.items || []);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, [setItems]);

  const search = async (val) => {
    setQ(val);
    try {
      const { data } = await axios.get(`${API}/experts/farmor/library?q=${encodeURIComponent(val)}`, { withCredentials: true });
      setItems(data.items || []);
    } catch { /* noop */ }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/experts/farmor/library/${id}`, { withCredentials: true });
      setDetail(data);
    } catch { toast.error('Kunde inte öppna'); }
  };

  const del = async (id, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('Ta bort denna post från biblioteket?')) return;
    try {
      await axios.delete(`${API}/experts/farmor/library/${id}`, { withCredentials: true });
      setItems((it) => it.filter((x) => x.id !== id));
      if (detail?.id === id) setDetail(null);
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-rose-50 to-amber-50 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-rose-700 tracking-wider">Farmors bibliotek</div>
            <div className="font-bold text-rose-900 text-lg">👵💕 Alla knep Farmor gett dig</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 border-b border-slate-100">
          <input
            type="text"
            value={q}
            onChange={(e) => search(e.target.value)}
            placeholder="Sök i biblioteket… (t.ex. spis, ättika, kalk)"
            data-testid="farmor-library-search"
            className="w-full text-sm rounded-full border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center text-slate-400 py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-8">
              Tomt än — nästa gång du ber Farmor om ett tips sparas det här 💜
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => openDetail(it.id)}
                  data-testid={`farmor-lib-item-${it.id}`}
                  className="w-full text-left rounded-xl border border-slate-200 hover:border-rose-300 hover:bg-rose-50/40 p-3 transition group relative"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{it.task || 'Bild-fråga'}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {it.source === 'image' ? '📸 Bild-analys' : `🧺 ${(it.materials || []).slice(0, 3).join(', ')}`}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1 line-clamp-2">{it.preview}</div>
                    </div>
                    <div className="text-[10px] text-slate-400 whitespace-nowrap">
                      {it.created_at ? new Date(it.created_at).toLocaleDateString('sv-SE') : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => del(it.id, e)}
                    className="absolute top-2 right-8 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100"
                    aria-label="Ta bort"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail sub-modal */}
      {detail && (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase font-bold text-rose-700 tracking-wider">Farmors knep</div>
              <button type="button" onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <h3 className="font-bold text-rose-900">{detail.task}</h3>
            <div className="mt-2 text-sm text-slate-700 space-y-2">
              {detail.data?.reply && <p className="italic bg-rose-50/50 border border-rose-100 rounded-lg p-2">{detail.data.reply}</p>}
              {detail.data?.intro && <p className="italic text-rose-800">👵 {detail.data.intro}</p>}
              {(detail.data?.materials || []).length > 0 && (
                <div className="text-xs">
                  <b>Ta fram:</b> {detail.data.materials.join(', ')}
                </div>
              )}
              {(detail.data?.steps || []).map((s, i) => (
                <div key={i} className="border-l-2 border-rose-200 pl-2">
                  <div className="text-xs font-bold">{s.order || i + 1}. {s.title}</div>
                  <div className="text-xs text-slate-600">{s.tip}</div>
                  {s.wait_while && <div className="text-[11px] text-amber-700 mt-0.5">💡 {s.wait_while}</div>}
                </div>
              ))}
              {detail.data?.farmors_kloka_ord && (
                <p className="text-xs italic text-rose-900 border-t border-rose-100 pt-2">👵💕 {detail.data.farmors_kloka_ord}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

